import { PdfLayout } from '../PdfLayout';
import { Bell, ArrowUpCircle, Mail, Archive, ListChecks } from 'lucide-react';

const benefits = [
  {
    icon: Bell,
    title: 'Alarm Rules on Any Tag',
    text: 'Greater than, less than, equal, between, outside a range or rate of change, with Info, Warning or Critical severity, on- and off-delays and a deadband so values at the limit don’t chatter.',
  },
  {
    icon: ArrowUpCircle,
    title: 'Multi-Stage Escalation',
    text: 'Chain steps with delays between them: text whoever is on call, wait ten minutes, then call the backup. Daily or weekly on-call rotations, quiet hours, and steps skipped once the alarm clears.',
  },
  {
    icon: Mail,
    title: 'Email, SMS, Voice and Webhooks',
    text: 'Email and webhooks from the appliance, SMS and text-to-speech calls relayed through Scada.Ink Cloud — to notification groups or a typed list. Any step can also run a script or workflow.',
  },
  {
    icon: Archive,
    title: 'Acknowledge and Shelve',
    text: 'Operators acknowledge one alarm or many at once, with an optional or required comment, and shelve a nuisance alarm for up to 72 hours with a reason. Shelves expire on their own.',
  },
  {
    icon: ListChecks,
    title: 'History and ISA-18.2 Metrics',
    text: 'Every occurrence recorded, searchable and exportable to CSV, plus alarm rates, floods, chattering and bad actors measured against ISA-18.2.',
  },
];

export function AlarmPipelines() {
  return (
    <PdfLayout
      category="Alarms & Notifications"
      headline="Never Miss a Critical Event with"
      headlineHighlight="Alarm Pipelines"
      description="Build escalation pipelines that reach the right person at the right time. Assign one pipeline to many alarm rules, filter by severity, and follow every step in the pipeline activity log."
      accentColor="#dc2626"
      benefits={benefits}
    >
      <AlarmMockup />
    </PdfLayout>
  );
}

function AlarmMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Alarm Pipeline Editor</span>
      </div>

      <svg viewBox="0 0 240 210" className="w-full">
        {/* Trigger */}
        <rect x="70" y="5" width="100" height="28" rx="6" fill="#dc2626" fillOpacity="0.1" stroke="#dc2626" strokeWidth="1.5" />
        <text x="120" y="16" textAnchor="middle" fontSize="7" fill="#dc2626" fontWeight="600">ALARM FIRES</text>
        <text x="120" y="26" textAnchor="middle" fontSize="6" fill="#64748b">Spindle Temp &gt; 70 (Critical)</text>

        {/* Arrow */}
        <line x1="120" y1="33" x2="120" y2="48" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="116,45 120,52 124,45" fill="#94a3b8" />

        {/* Step 1 */}
        <rect x="40" y="55" width="160" height="32" rx="6" fill="#ca8a04" fillOpacity="0.08" stroke="#ca8a04" strokeWidth="1.5" />
        <text x="120" y="67" textAnchor="middle" fontSize="7" fill="#ca8a04" fontWeight="600">Text the Technician</text>
        <text x="120" y="78" textAnchor="middle" fontSize="6" fill="#64748b">Send SMS · then Delay 10 min</text>

        {/* Timer icon */}
        <circle cx="205" cy="71" r="8" fill="#ca8a04" fillOpacity="0.1" />
        <text x="205" y="74" textAnchor="middle" fontSize="7" fill="#ca8a04">⏱</text>

        {/* Arrow */}
        <line x1="120" y1="87" x2="120" y2="102" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="116,99 120,106 124,99" fill="#94a3b8" />
        <text x="135" y="97" fontSize="6" fill="#dc2626">Still active</text>

        {/* Step 2 */}
        <rect x="40" y="109" width="160" height="32" rx="6" fill="#dc2626" fillOpacity="0.08" stroke="#dc2626" strokeWidth="1.5" />
        <text x="120" y="121" textAnchor="middle" fontSize="7" fill="#dc2626" fontWeight="600">Call the Supervisor</text>
        <text x="120" y="132" textAnchor="middle" fontSize="6" fill="#64748b">Voice call · then Delay 10 min</text>

        {/* Arrow */}
        <line x1="120" y1="141" x2="120" y2="156" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="116,153 120,160 124,153" fill="#94a3b8" />
        <text x="135" y="151" fontSize="6" fill="#dc2626">Still active</text>

        {/* Step 3 */}
        <rect x="40" y="163" width="160" height="32" rx="6" fill="#7c3aed" fillOpacity="0.08" stroke="#7c3aed" strokeWidth="1.5" />
        <text x="120" y="175" textAnchor="middle" fontSize="7" fill="#7c3aed" fontWeight="600">Escalate to Plant Managers</text>
        <text x="120" y="186" textAnchor="middle" fontSize="6" fill="#64748b">Email to notification group</text>

        {/* Side labels */}
        <text x="28" y="74" textAnchor="end" fontSize="6" fill="#ca8a04" fontWeight="500">SMS</text>
        <text x="28" y="128" textAnchor="end" fontSize="6" fill="#dc2626" fontWeight="500">Voice</text>
        <text x="28" y="182" textAnchor="end" fontSize="6" fill="#7c3aed" fontWeight="500">Email</text>
      </svg>

      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[8px] text-text-muted">5 Steps · 3 Channels</span>
        <span className="text-[8px] text-status-running font-medium">● Enabled</span>
      </div>
    </div>
  );
}
